/**
 * /app/equipo — Gobernanza de usuarios, roles y permisos (GA-04).
 * Backend: /api/team + /api/tenant/roles + assertSmStaffPermission.
 * Alta = password-at-create (sin email invite hasta provider configurado).
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Header, DataTable, EmptyState, Loading } from "@/components/ds";
import type { DataTableColumn } from "@/components/ds";
import { CrudEditorShell } from "@/components/workspace/CrudEditorShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FieldLabel } from "@/components/help/FieldHelpTip";
import {
  UserPlus,
  Pencil,
  Trash2,
  Shield,
  ShieldCheck,
  ShieldOff,
  Crown,
  ChevronDown,
  ChevronUp,
  Eye,
  Plus,
  Users,
  Copy,
} from "lucide-react";
import { apiGetJson, apiSendJson, apiDelete } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import {
  OPERATIONAL_PERMISSION_LABELS,
  OPERATIONAL_PERMISSION_DOMAINS,
  PERMISSION_CAPABILITY_SPECS,
  CAPABILITY_LEVEL_SHORT_LABELS,
  resolveCapabilityLevel,
  capabilityLevelToBoolean,
  isPermissionUsable,
  type CapabilityLevel,
} from "@structura/core/operational";
import type { OperationalPermission } from "@structura/core/operational";
import { useTenantProductFlags } from "@/hooks/use-tenant-product-flags";

/**
 * SCRUM-149 — Feature flag de capability levels granulares.
 * true = mostrar selector de nivel (Lectura / Edición / Control total) donde aplica.
 * false = comportamiento boolean anterior.
 */
const CAPABILITY_LEVELS_FF = true;
import { resolveEffectiveAccessView } from "@/lib/tenant-access-governance";
import { permissionAccessKind, permissionAccessKindLabel } from "@/lib/merchant-feature-copy";
import { toast } from "sonner";
import { useAppDialogs } from "@/components/ui/app-dialogs";

export const Route = createFileRoute("/app/equipo")({
  component: EquipoPage,
  head: () => ({ meta: [{ title: "Equipo" }] }),
});

interface LocationScopeEntry {
  locationId: string;
  /** null = usa el rol global del usuario en esa sucursal. */
  operationalRoleId: string | null;
}

interface TeamMember {
  id: string;
  email: string;
  name: string;
  role: string;
  active: boolean;
  username: string | null;
  operationalRoleId: string | null;
  permissionOverrides: Record<string, boolean> | null;
  lastAccessAt?: string | null;
  /** SCRUM-64: sucursales a las que está restringido. [] = acceso a todas. */
  locationIds?: string[];
  /** Fase 7: sucursal + rol por sucursal (override del rol global, si hay). */
  locationScope?: LocationScopeEntry[];
}

interface LocationOption {
  id: string;
  name: string;
}

interface RoleTemplate {
  id: string;
  name: string;
  jobTitle?: string | null;
  operationalRoleId: string | null;
  permissions: string[];
  /** SCRUM-477/478/479: nivel (read/write/admin) elegido por permiso, cuando soporta sub-niveles. */
  permissionLevels?: Record<string, string>;
  isSystemPreset: boolean;
  assignedUsers?: number;
}

interface SeatUsage {
  used: number;
  limit: number | null;
  unlimited: boolean;
  label: string;
}

const ALL_PERMISSIONS = Object.keys(OPERATIONAL_PERMISSION_LABELS) as OperationalPermission[];

function EquipoPage() {
  const tid = useWorkspaceTenantId();
  // SCRUM-435/440: no ofrecer permisos que no tienen efecto real para este
  // tenant (add-ons apagados) — solo cubre los pocos permisos con un flag
  // conocido y verificable (dine_in_tables, booking_widget, growth_campaigns);
  // el resto de permisos son core del producto y siempre se muestran.
  const productFlags = useTenantProductFlags();

  const [members, setMembers] = useState<TeamMember[]>([]);
  const { confirm } = useAppDialogs();
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [templates, setTemplates] = useState<RoleTemplate[]>([]);
  const [seats, setSeats] = useState<SeatUsage | null>(null);
  const [canOverridePermissions, setCanOverridePermissions] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("usuarios");
  const [search, setSearch] = useState("");

  const [inviteOpen, setInviteOpen] = useState(false);
  const [editMember, setEditMember] = useState<TeamMember | null>(null);
  const [effectiveMember, setEffectiveMember] = useState<TeamMember | null>(null);
  const [showPermissionDetail, setShowPermissionDetail] = useState(false);

  const [inviteForm, setInviteForm] = useState({
    name: "",
    email: "",
    password: "",
    operationalRoleId: "",
  });
  const [editRoleId, setEditRoleId] = useState("");
  const [editOverrides, setEditOverrides] = useState<Record<string, boolean | null>>({});
  /** SCRUM-149: capability levels — estado de UI (no persiste hasta que se aplique la migración DB). */
  const [editCapabilityLevels, setEditCapabilityLevels] = useState<Record<string, CapabilityLevel>>({});
  const [editActive, setEditActive] = useState(true);
  const [editLocationIds, setEditLocationIds] = useState<string[]>([]);
  /** Fase 7: override de rol por sucursal. Ausente/undefined = usa el rol general. */
  const [editLocationRoles, setEditLocationRoles] = useState<Record<string, string | null>>({});

  const [roleEditorOpen, setRoleEditorOpen] = useState(false);
  const [roleEditing, setRoleEditing] = useState<RoleTemplate | null>(null);
  const [roleForm, setRoleForm] = useState({
    name: "",
    description: "",
    permissions: [] as string[],
    permissionLevels: {} as Record<string, CapabilityLevel>,
  });

  const load = async () => {
    if (!tid) return;
    setLoading(true);
    try {
      const [teamRes, rolesRes] = await Promise.all([
        apiGetJson<{
          members?: TeamMember[];
          locations?: LocationOption[];
          seats?: SeatUsage;
          capabilities?: { canOverridePermissions?: boolean };
        }>("/api/team", tid),
        apiGetJson<{ templates?: RoleTemplate[] }>("/api/tenant/roles", tid),
      ]);
      setMembers(teamRes.members ?? []);
      setLocations(teamRes.locations ?? []);
      setSeats(teamRes.seats ?? null);
      setCanOverridePermissions(teamRes.capabilities?.canOverridePermissions ?? false);
      setTemplates(
        (rolesRes.templates ?? []).filter((t) => t.operationalRoleId !== "owner"),
      );
    } catch {
      toast.error("Error al cargar equipo");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [tid]);

  const staffTemplates = useMemo(
    () => templates.filter((t) => Boolean(t.operationalRoleId)),
    [templates],
  );

  const selectedInviteTpl = staffTemplates.find(
    (t) => t.operationalRoleId === inviteForm.operationalRoleId,
  );

  const filteredMembers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return members;
    return members.filter(
      (m) =>
        m.name.toLowerCase().includes(q) ||
        m.email.toLowerCase().includes(q) ||
        (m.operationalRoleId ?? "").toLowerCase().includes(q),
    );
  }, [members, search]);

  const effectivePermissions = (member: TeamMember): OperationalPermission[] => {
    const tpl = templates.find((t) => t.operationalRoleId === member.operationalRoleId);
    const base = new Set<string>(tpl?.permissions ?? []);
    for (const [perm, val] of Object.entries(member.permissionOverrides ?? {})) {
      if (val === true) base.add(perm);
      else if (val === false) base.delete(perm);
    }
    return Array.from(base) as OperationalPermission[];
  };

  const handleInvite = async () => {
    if (!tid) return;
    if (!inviteForm.name.trim() || !inviteForm.email.trim() || !inviteForm.password.trim()) {
      toast.error("Nombre, email y contraseña son obligatorios");
      return;
    }
    setSaving(true);
    try {
      const r = (await apiSendJson(
        "/api/team",
        "POST",
        {
          name: inviteForm.name.trim(),
          email: inviteForm.email.trim().toLowerCase(),
          password: inviteForm.password,
          operationalRoleId: inviteForm.operationalRoleId || null,
        },
        tid,
      )) as { member?: TeamMember; error?: string; code?: string };

      if (r.error) {
        toast.error(r.error);
        return;
      }
      toast.success("Usuario agregado al equipo");
      setInviteOpen(false);
      setInviteForm({ name: "", email: "", password: "", operationalRoleId: "" });
      await load();
    } catch {
      toast.error("Error al agregar usuario");
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (member: TeamMember) => {
    setEditMember(member);
    setEditRoleId(member.operationalRoleId ?? "");
    setEditActive(member.active);
    setEditOverrides(
      Object.fromEntries(Object.entries(member.permissionOverrides ?? {}).map(([k, v]) => [k, v])),
    );
    setEditCapabilityLevels({});
    setEditLocationIds(member.locationIds ?? []);
    setEditLocationRoles(
      Object.fromEntries(
        (member.locationScope ?? [])
          .filter((e) => e.operationalRoleId !== null)
          .map((e) => [e.locationId, e.operationalRoleId]),
      ),
    );
    setShowPermissionDetail(false);
  };

  const handleSaveEdit = async () => {
    if (!tid || !editMember) return;
    setSaving(true);
    try {
      const patch: {
        operationalRoleId?: string | null;
        permissionOverrides?: Record<string, boolean | null> | null;
        /** SCRUM-149: capability levels granulares. */
        permissionCapabilities?: Record<string, CapabilityLevel> | null;
        active?: boolean;
        locationIds?: string[];
        locationRoles?: Record<string, string | null>;
      } = {};

      if (editRoleId !== (editMember.operationalRoleId ?? "")) {
        patch.operationalRoleId = editRoleId || null;
      }
      if (editActive !== editMember.active) {
        patch.active = editActive;
      }
      const originalLocationIds = [...(editMember.locationIds ?? [])].sort();
      const nextLocationIds = [...editLocationIds].sort();
      if (
        originalLocationIds.length !== nextLocationIds.length ||
        originalLocationIds.some((id, i) => id !== nextLocationIds[i])
      ) {
        patch.locationIds = editLocationIds;
      }

      const originalLocationRoles: Record<string, string | null> = Object.fromEntries(
        (editMember.locationScope ?? [])
          .filter((e) => e.operationalRoleId !== null)
          .map((e) => [e.locationId, e.operationalRoleId]),
      );
      const locationRolesChanged =
        Object.keys(originalLocationRoles).length !== Object.keys(editLocationRoles).length ||
        Object.entries(editLocationRoles).some(([id, role]) => originalLocationRoles[id] !== role);
      if (locationRolesChanged) {
        // Incluye null explícito para las que tenían override y ahora vuelven al rol general.
        const removed = Object.keys(originalLocationRoles).filter((id) => !(id in editLocationRoles));
        patch.locationRoles = { ...editLocationRoles, ...Object.fromEntries(removed.map((id) => [id, null])) };
      }

      if (canOverridePermissions) {
        const cleaned: Record<string, boolean | null> = {};
        for (const perm of ALL_PERMISSIONS) {
          const val = editOverrides[perm];
          if (val === true || val === false) cleaned[perm] = val;
        }
        patch.permissionOverrides = Object.keys(cleaned).length > 0 ? cleaned : null;

        // SCRUM-149: incluir capability levels cuando el FF está activo.
        if (CAPABILITY_LEVELS_FF && Object.keys(editCapabilityLevels).length > 0) {
          patch.permissionCapabilities = { ...editCapabilityLevels };
        }
      }

      if (Object.keys(patch).length === 0) {
        toast.info("Sin cambios");
        setEditMember(null);
        return;
      }

      const r = (await apiSendJson(`/api/team?userId=${editMember.id}`, "PATCH", patch, tid)) as {
        ok?: boolean;
        error?: string;
      };

      if (r.error) {
        toast.error(r.error);
        return;
      }
      toast.success("Usuario actualizado");
      setEditMember(null);
      setEditCapabilityLevels({});
      await load();
    } catch {
      toast.error("Error al actualizar usuario");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (member: TeamMember) => {
    if (!tid) return;
    if (!(await confirm({ title: `¿Eliminar a ${member.name} del equipo?`, description: "Esta acción no se puede deshacer.", destructive: true, confirmLabel: "Eliminar" }))) return;
    try {
      await apiDelete(`/api/team?userId=${member.id}`, tid);
      toast.success("Usuario eliminado");
      await load();
    } catch {
      toast.error("Error al eliminar usuario");
    }
  };

  const openCreateRole = () => {
    setRoleEditing(null);
    setRoleForm({ name: "", description: "", permissions: [], permissionLevels: {} });
    setRoleEditorOpen(true);
  };

  const openEditRole = (tpl: RoleTemplate) => {
    if (tpl.isSystemPreset) {
      toast.info("Los roles de sistema no se editan. Creá un rol personalizado o usá permisos adicionales.");
      return;
    }
    setRoleEditing(tpl);
    setRoleForm({
      name: tpl.name,
      description: tpl.jobTitle ?? "",
      permissions: [...tpl.permissions],
      permissionLevels: { ...(tpl.permissionLevels as Record<string, CapabilityLevel> | undefined) },
    });
    setRoleEditorOpen(true);
  };

  /** SCRUM-439: crear un rol personalizado a partir de un preset del sistema — precarga
   * el editor con el mismo nombre/permisos, pero como alta nueva (roleEditing = null),
   * nunca modifica el preset original. */
  const openCreateRoleFromPreset = (tpl: RoleTemplate) => {
    setRoleEditing(null);
    setRoleForm({
      name: `${tpl.name} (copia)`,
      description: tpl.jobTitle ?? "",
      permissions: [...tpl.permissions],
      permissionLevels: { ...(tpl.permissionLevels as Record<string, CapabilityLevel> | undefined) },
    });
    setRoleEditorOpen(true);
  };

  const handleSaveRole = async () => {
    if (!tid) return;
    if (!roleForm.name.trim()) {
      toast.error("Nombre del rol requerido");
      return;
    }
    setSaving(true);
    try {
      if (roleEditing) {
        const assigned = roleEditing.assignedUsers ?? 0;
        let confirmImpact = true;
        if (assigned > 0) {
          confirmImpact = await confirm({
            title: `Este cambio afectará a ${assigned} usuario${assigned === 1 ? "" : "s"}. ¿Continuar?`,
            confirmLabel: "Continuar",
          });
          if (!confirmImpact) {
            setSaving(false);
            return;
          }
        }
        const r = (await apiSendJson(
          `/api/tenant/roles?id=${roleEditing.id}`,
          "PATCH",
          {
            name: roleForm.name.trim(),
            description: roleForm.description.trim() || undefined,
            permissions: roleForm.permissions,
            permissionLevels: roleForm.permissionLevels,
            confirmImpact: true,
          },
          tid,
        )) as { error?: string };
        if (r.error) {
          toast.error(r.error);
          return;
        }
        toast.success("Rol actualizado");
      } else {
        const r = (await apiSendJson(
          "/api/tenant/roles",
          "POST",
          {
            name: roleForm.name.trim(),
            description: roleForm.description.trim() || undefined,
            permissions: roleForm.permissions,
            permissionLevels: roleForm.permissionLevels,
          },
          tid,
        )) as { error?: string };
        if (r.error) {
          toast.error(r.error);
          return;
        }
        toast.success("Rol creado");
      }
      setRoleEditorOpen(false);
      await load();
    } catch {
      toast.error("Error al guardar rol");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async (tpl: RoleTemplate) => {
    if (!tid || tpl.isSystemPreset) return;
    if (!(await confirm({ title: `¿Eliminar el rol «${tpl.name}»?`, destructive: true, confirmLabel: "Eliminar" }))) return;
    try {
      await apiDelete(`/api/tenant/roles?id=${tpl.id}`, tid);
      toast.success("Rol eliminado");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo eliminar el rol");
    }
  };

  const PermissionToggle = ({
    perm,
    baseHas,
  }: {
    perm: OperationalPermission;
    baseHas: boolean;
  }) => {
    const override = editOverrides[perm];
    const effective = override === true ? true : override === false ? false : baseHas;
    const spec = PERMISSION_CAPABILITY_SPECS[perm];

    const cycle = () => {
      if (override === undefined) {
        setEditOverrides((prev) => ({ ...prev, [perm]: true }));
      } else if (override === true && !baseHas) {
        setEditOverrides((prev) => {
          const n = { ...prev };
          delete n[perm];
          return n;
        });
      } else if (override === true && baseHas) {
        setEditOverrides((prev) => ({ ...prev, [perm]: false }));
      } else if (override === false) {
        setEditOverrides((prev) => {
          const n = { ...prev };
          delete n[perm];
          return n;
        });
      }
    };

    const Icon = override === true ? ShieldCheck : override === false ? ShieldOff : Shield;
    const color =
      override === true
        ? "text-green-500"
        : override === false
          ? "text-red-400"
          : effective
            ? "text-muted-foreground"
            : "text-muted-foreground/40";

    // SCRUM-149: nivel seleccionado para este permiso (si FF activo y soporta sub-niveles).
    const selectedLevel = editCapabilityLevels[perm] as CapabilityLevel | undefined;
    const displayLevel = selectedLevel ?? (effective ? spec.booleanEquivalentLevel : "none");

    const handleLevelChange = (level: CapabilityLevel) => {
      setEditCapabilityLevels((prev) => ({ ...prev, [perm]: level }));
      // Sincroniza el boolean override para backward compat.
      const boolVal = capabilityLevelToBoolean(level);
      if (!boolVal && !baseHas) {
        setEditOverrides((prev) => { const n = { ...prev }; delete n[perm]; return n; });
      } else if (!boolVal && baseHas) {
        setEditOverrides((prev) => ({ ...prev, [perm]: false }));
      } else if (boolVal && !baseHas) {
        setEditOverrides((prev) => ({ ...prev, [perm]: true }));
      } else {
        setEditOverrides((prev) => { const n = { ...prev }; delete n[perm]; return n; });
      }
    };

    return (
      <div className="flex w-full items-start gap-2.5 rounded-md px-2 py-1.5 hover:bg-muted/50">
        <button
          type="button"
          onClick={cycle}
          className="mt-0.5 shrink-0"
          aria-label={`Ciclar permiso ${OPERATIONAL_PERMISSION_LABELS[perm]}`}
        >
          <Icon className={`h-3.5 w-3.5 ${color}`} />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className={`text-xs ${effective ? "" : "text-muted-foreground/50"}`}>
              {OPERATIONAL_PERMISSION_LABELS[perm]}
            </span>
            {/* SCRUM-149: selector de nivel cuando FF activo, soporta sub-niveles y hay capacidad de override. */}
            {CAPABILITY_LEVELS_FF && canOverridePermissions && spec.supportsSubLevels ? (
              <select
                value={displayLevel}
                onChange={(e) => handleLevelChange(e.target.value as CapabilityLevel)}
                className="h-6 rounded border border-input bg-transparent px-1.5 text-[10px] text-muted-foreground"
                data-testid={`capability-level-select-${perm}`}
              >
                {(["none", "read", "write", "admin"] as CapabilityLevel[]).map((lvl) => (
                  <option key={lvl} value={lvl}>
                    {CAPABILITY_LEVEL_SHORT_LABELS[lvl]}
                  </option>
                ))}
              </select>
            ) : (
              <span className="text-[10px] text-muted-foreground">
                {effective ? permissionAccessKindLabel(permissionAccessKind(perm)) : "Sin acceso"}
              </span>
            )}
          </div>
          {/* SCRUM-149: descripción del nivel seleccionado cuando sub-niveles disponibles. */}
          {CAPABILITY_LEVELS_FF && spec.supportsSubLevels && displayLevel !== "none" && (
            <p className="mt-0.5 text-[10px] text-muted-foreground/70">
              {displayLevel === "read" && spec.readDescription}
              {displayLevel === "write" && spec.writeDescription}
              {displayLevel === "admin" && spec.adminDescription}
            </p>
          )}
        </div>
        {override !== undefined && (
          <span
            className={`mt-0.5 shrink-0 text-[10px] font-medium ${override === true ? "text-green-500" : "text-red-400"}`}
          >
            {override === true ? "Adicional" : "Restringido"}
          </span>
        )}
      </div>
    );
  };

  const columns: DataTableColumn<TeamMember>[] = [
    {
      key: "member",
      header: "Usuario",
      render: (m) => (
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold uppercase text-muted-foreground">
            {(m.name || m.email).charAt(0)}
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium leading-tight">
              {m.name}
              {m.role === "owner" && <Crown className="ml-1.5 inline h-3.5 w-3.5 text-amber-500" />}
            </p>
            <p className="truncate text-xs text-muted-foreground">{m.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: "role",
      header: "Rol",
      render: (m) => {
        if (m.role === "owner")
          return (
            <Badge variant="outline" className="text-amber-600">
              Dueño
            </Badge>
          );
        const tpl = templates.find((t) => t.operationalRoleId === m.operationalRoleId);
        return tpl ? (
          <Badge variant="secondary">{tpl.name}</Badge>
        ) : (
          <span className="text-xs text-muted-foreground">Sin rol</span>
        );
      },
    },
    ...(locations.length > 1
      ? [
          {
            key: "locations",
            header: "Sucursales",
            render: (m: TeamMember) => {
              if (m.role === "owner" || !m.locationIds?.length) {
                return <span className="text-xs text-muted-foreground">Todas</span>;
              }
              return (
                <Badge variant="outline" className="text-[11px]">
                  {m.locationIds.length === 1
                    ? locations.find((l) => l.id === m.locationIds![0])?.name ?? "1 sucursal"
                    : `${m.locationIds.length} sucursales`}
                </Badge>
              );
            },
          } satisfies DataTableColumn<TeamMember>,
        ]
      : []),
    {
      key: "perms",
      header: "Acceso",
      className: "text-right tabular-nums text-muted-foreground text-xs",
      render: (m) => {
        if (m.role === "owner") return <span className="text-amber-500">Completo</span>;
        const perms = effectivePermissions(m);
        const overrideCount = Object.keys(m.permissionOverrides ?? {}).length;
        return (
          <div className="flex items-center justify-end gap-1.5">
            <span>
              {perms.length === 0
                ? "Sin acceso"
                : `${perms.length} de ${ALL_PERMISSIONS.length}`}
            </span>
            {overrideCount > 0 && (
              <Badge variant="outline" className="text-[10px] text-primary">
                +{overrideCount}
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      key: "status",
      header: "Estado",
      render: (m) =>
        m.active ? (
          <Badge variant="outline" className="text-green-600">
            Activo
          </Badge>
        ) : (
          <Badge variant="outline" className="text-muted-foreground">
            Inactivo
          </Badge>
        ),
    },
    {
      key: "actions",
      header: "",
      className: "w-24",
      render: (m) => (
        <div className="flex items-center gap-1">
          <button
            onClick={() => setEffectiveMember(m)}
            className="rounded p-1 text-muted-foreground hover:text-foreground"
            title="Acceso efectivo"
          >
            <Eye className="h-3.5 w-3.5" />
          </button>
          {m.role !== "owner" ? (
            <>
              <button
                onClick={() => openEdit(m)}
                className="rounded p-1 text-muted-foreground hover:text-foreground"
                title="Editar"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => void handleDelete(m)}
                className="rounded p-1 text-muted-foreground hover:text-red-400"
                title="Eliminar"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </>
          ) : null}
        </div>
      ),
    },
  ];

  const roleColumns: DataTableColumn<RoleTemplate>[] = [
    {
      key: "name",
      header: "Rol",
      render: (t) => (
        <div>
          <p className="text-sm font-medium">{t.name}</p>
          {t.jobTitle ? <p className="text-xs text-muted-foreground">{t.jobTitle}</p> : null}
        </div>
      ),
    },
    {
      key: "type",
      header: "Tipo",
      render: (t) =>
        t.isSystemPreset ? (
          <Badge variant="outline">Plantilla</Badge>
        ) : (
          <Badge variant="secondary">Personalizado</Badge>
        ),
    },
    {
      key: "perms",
      header: "Permisos",
      className: "tabular-nums text-xs text-muted-foreground",
      render: (t) => `${t.permissions.length}`,
    },
    {
      key: "users",
      header: "Usuarios",
      className: "tabular-nums text-xs",
      render: (t) => t.assignedUsers ?? 0,
    },
    {
      key: "actions",
      header: "",
      className: "w-20",
      render: (t) =>
        t.isSystemPreset ? (
          <button
            onClick={() => openCreateRoleFromPreset(t)}
            className="rounded p-1 text-muted-foreground hover:text-foreground"
            title="Crear rol personalizado a partir de esta plantilla"
            data-testid="role-template-duplicate"
          >
            <Copy className="h-3.5 w-3.5" />
          </button>
        ) : (
          <div className="flex gap-1">
            <button
              onClick={() => openEditRole(t)}
              className="rounded p-1 text-muted-foreground hover:text-foreground"
              title="Editar"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => void handleDeleteRole(t)}
              className="rounded p-1 text-muted-foreground hover:text-red-400"
              title="Eliminar"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ),
    },
  ];

  const ownerCount = members.filter((m) => m.role === "owner").length;
  const staffCount = members.filter((m) => m.role === "staff").length;

  /** SCRUM-436: resumen de qué va a cambiar, antes de confirmar el guardado —
   * solo lista lo que realmente difiere del estado original de editMember. */
  const editSummaryLines: string[] = (() => {
    if (!editMember) return [];
    const lines: string[] = [];
    const roleName = (roleId: string) =>
      staffTemplates.find((t) => t.operationalRoleId === roleId)?.name ?? "Sin rol";
    if (editRoleId !== (editMember.operationalRoleId ?? "")) {
      lines.push(`Rol: ${roleName(editMember.operationalRoleId ?? "")} → ${roleName(editRoleId)}`);
    }
    if (editActive !== editMember.active) {
      lines.push(editActive ? "Se reactiva el usuario" : "Se desactiva el usuario (deja de consumir plaza)");
    }
    const originalIds = [...(editMember.locationIds ?? [])].sort();
    const nextIds = [...editLocationIds].sort();
    if (JSON.stringify(originalIds) !== JSON.stringify(nextIds)) {
      lines.push(
        nextIds.length === 0
          ? "Sucursales: acceso a todas (sin restricción)"
          : `Sucursales: acceso limitado a ${nextIds.length} ${nextIds.length === 1 ? "sucursal" : "sucursales"}`,
      );
    }
    const originalRoles = Object.fromEntries(
      (editMember.locationScope ?? []).filter((e) => e.operationalRoleId != null).map((e) => [e.locationId, e.operationalRoleId]),
    );
    for (const [locId, roleId] of Object.entries(editLocationRoles)) {
      if (originalRoles[locId] !== roleId) {
        const locName = locations.find((l) => l.id === locId)?.name ?? locId;
        lines.push(`${locName}: rol distinto al general → ${roleName(roleId ?? "")}`);
      }
    }
    return lines;
  })();

  const effectiveView = effectiveMember
    ? resolveEffectiveAccessView({
        isOwner: effectiveMember.role === "owner",
        roleName:
          templates.find((t) => t.operationalRoleId === effectiveMember.operationalRoleId)?.name ??
          null,
        roleId: effectiveMember.operationalRoleId,
        rolePermissions: (templates.find(
          (t) => t.operationalRoleId === effectiveMember.operationalRoleId,
        )?.permissions ?? []) as OperationalPermission[],
        overrides: effectiveMember.permissionOverrides,
      })
    : null;

  return (
    <div>
      <Header
        title="Equipo"
        subtitle="Quién puede entrar al panel y qué puede hacer en cada área. El rol define el acceso; los permisos definen las acciones."
        actions={
          <Button size="sm" onClick={() => setInviteOpen(true)}>
            <UserPlus className="mr-1.5 h-4 w-4" />
            Agregar usuario
          </Button>
        }
      />
      <div className="px-6 pt-3">
        <Link
          to="/app/settings"
          search={{ tab: undefined }}
          className="inline-flex w-fit items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          ← Volver a Configuración
        </Link>
      </div>

      {loading ? (
        <Loading variant="list" />
      ) : (
        <div className="px-4 pb-8 pt-4">
          {seats && !seats.unlimited ? (
            <div className="mb-4 rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm">
              <span className="font-medium">{seats.label}</span>
              {seats.limit != null && seats.used >= seats.limit ? (
                <span className="ml-2 text-amber-600">
                  Llegaste al máximo. Desactivá un usuario o mejorá tu plan.
                </span>
              ) : null}
            </div>
          ) : null}

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="usuarios">Usuarios</TabsTrigger>
              <TabsTrigger value="roles">Roles</TabsTrigger>
            </TabsList>

            <TabsContent value="usuarios" className="mt-4 space-y-3">
              <Input
                placeholder="Buscar por nombre o email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="max-w-sm"
              />
              <CrudEditorShell
                open={inviteOpen}
                onOpenChange={(open) => {
                  setInviteOpen(open);
                  if (!open) setInviteForm({ name: "", email: "", password: "", operationalRoleId: "" });
                }}
                title="Agregar usuario"
                size="md"
                list={
                  <DataTable
                    columns={columns}
                    rows={filteredMembers}
                    getRowKey={(m) => m.id}
                    emptyState={
                      <EmptyState
                        icon={Users}
                        title="Invitá a tu equipo"
                        description="Agregá colaboradores con un rol. Cada usuario activo consume una plaza de tu plan."
                        action={
                          <Button size="sm" onClick={() => setInviteOpen(true)}>
                            Agregar usuario
                          </Button>
                        }
                      />
                    }
                  />
                }
                footer={
                  <>
                    <Button type="button" variant="outline" onClick={() => setInviteOpen(false)} disabled={saving}>
                      Cancelar
                    </Button>
                    <Button type="button" onClick={() => void handleInvite()} disabled={saving}>
                      {saving ? "Guardando…" : "Agregar"}
                    </Button>
                  </>
                }
              >
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <FieldLabel required>Nombre</FieldLabel>
                    <Input
                      value={inviteForm.name}
                      onChange={(e) => setInviteForm((f) => ({ ...f, name: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <FieldLabel required>Email</FieldLabel>
                    <Input
                      type="email"
                      value={inviteForm.email}
                      onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <FieldLabel required>Contraseña temporal</FieldLabel>
                    <Input
                      type="password"
                      value={inviteForm.password}
                      onChange={(e) => setInviteForm((f) => ({ ...f, password: e.target.value }))}
                    />
                    <p className="text-xs text-muted-foreground">
                      El usuario inicia sesión con este email y contraseña. El envío por correo requiere el proveedor de email configurado.
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <FieldLabel tipKey="team.role">Rol</FieldLabel>
                    <select
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                      value={inviteForm.operationalRoleId}
                      onChange={(e) => setInviteForm((f) => ({ ...f, operationalRoleId: e.target.value }))}
                    >
                      <option value="">Sin rol (sin acceso operativo)</option>
                      {staffTemplates.map((t) => (
                        <option key={t.id} value={t.operationalRoleId ?? ""}>
                          {t.name}
                          {t.isSystemPreset ? "" : " (personalizado)"}
                        </option>
                      ))}
                    </select>
                    {selectedInviteTpl ? (
                      <div className="rounded-md border border-border bg-muted/20 p-3 text-xs">
                        <p className="font-medium text-foreground">{selectedInviteTpl.name}</p>
                        {selectedInviteTpl.jobTitle ? (
                          <p className="mt-0.5 text-muted-foreground">{selectedInviteTpl.jobTitle}</p>
                        ) : null}
                        <p className="mt-2 text-muted-foreground">
                          Acceso:{" "}
                          {selectedInviteTpl.permissions
                            .slice(0, 6)
                            .map((p) => OPERATIONAL_PERMISSION_LABELS[p as OperationalPermission] ?? p)
                            .join(" · ")}
                          {selectedInviteTpl.permissions.length > 6
                            ? ` · +${selectedInviteTpl.permissions.length - 6}`
                            : ""}
                        </p>
                      </div>
                    ) : null}
                  </div>
                </div>
              </CrudEditorShell>
            </TabsContent>

            <TabsContent value="roles" className="mt-4 space-y-3">
              <div className="flex justify-end">
                <Button size="sm" onClick={openCreateRole}>
                  <Plus className="mr-1.5 h-4 w-4" />
                  Crear rol
                </Button>
              </div>
              <DataTable
                columns={roleColumns}
                rows={templates}
                getRowKey={(t) => t.id}
                emptyState={
                  <EmptyState
                    title="No hay roles personalizados"
                    description="Los roles te permiten crear plantillas de acceso para tus usuarios."
                    action={
                      <Button size="sm" onClick={openCreateRole}>
                        Crear rol
                      </Button>
                    }
                  />
                }
              />
            </TabsContent>
          </Tabs>

          {/* Edit member */}
          <CrudEditorShell
            open={!!editMember}
            onOpenChange={(open) => {
              if (!open) setEditMember(null);
            }}
            title={editMember ? `Editar · ${editMember.name}` : "Editar"}
            size="md"
            list={null}
            footer={
              <>
                <Button type="button" variant="outline" onClick={() => setEditMember(null)} disabled={saving}>
                  Cancelar
                </Button>
                <Button type="button" onClick={() => void handleSaveEdit()} disabled={saving}>
                  {saving ? "Guardando…" : "Guardar"}
                </Button>
              </>
            }
          >
            {editMember ? (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <FieldLabel tipKey="team.role">Rol</FieldLabel>
                  <select
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                    value={editRoleId}
                    onChange={(e) => setEditRoleId(e.target.value)}
                  >
                    <option value="">Sin rol</option>
                    {staffTemplates.map((t) => (
                      <option key={t.id} value={t.operationalRoleId ?? ""}>
                        {t.name} ({t.permissions.length} permisos)
                      </option>
                    ))}
                  </select>
                </div>

                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={editActive}
                    onChange={(e) => setEditActive(e.target.checked)}
                  />
                  Usuario activo (consume plaza del plan)
                </label>

                {locations.length > 1 && editMember.role !== "owner" ? (
                  <div className="space-y-1.5 rounded-md border border-border p-3">
                    <p className="text-sm font-medium">¿En qué sucursales puede trabajar?</p>
                    <p className="text-xs text-muted-foreground">
                      Sin marcar ninguna, tiene acceso a todas. Marcá una o varias para limitarlo. Opcionalmente, asignale un rol distinto al general en cada una.
                    </p>
                    <div className="grid gap-2 pt-1 sm:grid-cols-2">
                      {locations.map((loc) => {
                        const checked = editLocationIds.includes(loc.id);
                        return (
                          <div key={loc.id} className="space-y-1">
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  setEditLocationIds((prev) =>
                                    e.target.checked
                                      ? [...prev, loc.id]
                                      : prev.filter((id) => id !== loc.id),
                                  );
                                  if (!e.target.checked) {
                                    setEditLocationRoles((prev) => {
                                      if (!(loc.id in prev)) return prev;
                                      const next = { ...prev };
                                      delete next[loc.id];
                                      return next;
                                    });
                                  }
                                }}
                              />
                              {loc.name}
                            </label>
                            {checked ? (
                              <select
                                className="ml-6 flex h-7 w-[calc(100%-1.5rem)] rounded-md border border-input bg-transparent px-2 text-xs"
                                value={editLocationRoles[loc.id] ?? ""}
                                onChange={(e) =>
                                  setEditLocationRoles((prev) => {
                                    const next = { ...prev };
                                    if (e.target.value) next[loc.id] = e.target.value;
                                    else delete next[loc.id];
                                    return next;
                                  })
                                }
                              >
                                <option value="">Usar el rol general{editRoleId ? "" : " (sin rol)"}</option>
                                {staffTemplates.map((t) => (
                                  <option key={t.id} value={t.operationalRoleId ?? ""}>
                                    {t.name} en {loc.name}
                                  </option>
                                ))}
                              </select>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}

                <div>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between text-sm font-medium"
                    onClick={() => setShowPermissionDetail((v) => !v)}
                  >
                    <span>Permisos adicionales</span>
                    {showPermissionDetail ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </button>
                  {showPermissionDetail ? (
                    <div className="mt-2 rounded-md border border-border">
                      {!canOverridePermissions && (
                        <p className="border-b border-border px-3 py-2 text-xs text-amber-600">
                          Esta función no está incluida en tu plan. Mejorá a Pro para ajustar permisos por usuario.
                        </p>
                      )}
                      <div
                        className={`divide-y divide-border/40 p-1 ${!canOverridePermissions ? "pointer-events-none opacity-50" : ""}`}
                      >
                        {OPERATIONAL_PERMISSION_DOMAINS.map((domain) => {
                          const baseTpl = templates.find(
                            (t) => t.operationalRoleId === (editRoleId || editMember.operationalRoleId),
                          );
                          return (
                            <div key={domain.id} className="py-2">
                              <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                {domain.label}
                              </p>
                              {domain.permissions.map((perm) => (
                                <PermissionToggle
                                  key={perm}
                                  perm={perm}
                                  baseHas={Boolean(baseTpl?.permissions.includes(perm))}
                                />
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>

                {editSummaryLines.length > 0 ? (
                  <div
                    className="rounded-md border border-primary/30 bg-primary/5 p-3 text-sm"
                    data-testid="team-edit-summary"
                  >
                    <p className="mb-1.5 text-xs font-semibold uppercase text-muted-foreground">
                      Vas a aplicar estos cambios
                    </p>
                    <ul className="space-y-1">
                      {editSummaryLines.map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            ) : null}
          </CrudEditorShell>

          {/* Effective access */}
          <CrudEditorShell
            open={!!effectiveMember}
            onOpenChange={(open) => {
              if (!open) setEffectiveMember(null);
            }}
            title={effectiveMember ? `Acceso efectivo · ${effectiveMember.name}` : "Acceso efectivo"}
            size="md"
            list={null}
            footer={
              <Button type="button" variant="outline" onClick={() => setEffectiveMember(null)}>
                Cerrar
              </Button>
            }
          >
            {effectiveView ? (
              <div className="space-y-4 text-sm">
                <ul className="space-y-1 text-muted-foreground">
                  {effectiveView.summaryLines.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
                {/* SCRUM-437: acceso efectivo por sucursal — cuando hay override de rol
                    en alguna sucursal, mostrar qué rol aplica ahí (distinto al global). */}
                {effectiveMember &&
                (effectiveMember.locationScope ?? []).some((e) => e.operationalRoleId != null) ? (
                  <div>
                    <p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">
                      Acceso por sucursal
                    </p>
                    <ul className="space-y-1">
                      {(effectiveMember.locationScope ?? [])
                        .filter((e) => e.operationalRoleId != null)
                        .map((e) => {
                          const locationName = locations.find((l) => l.id === e.locationId)?.name ?? e.locationId;
                          const roleName =
                            templates.find((t) => t.operationalRoleId === e.operationalRoleId)?.name ??
                            e.operationalRoleId;
                          return (
                            <li key={e.locationId} data-testid="effective-access-location-role">
                              <span className="font-medium">{locationName}:</span> {roleName} (en vez del rol global)
                            </li>
                          );
                        })}
                    </ul>
                  </div>
                ) : null}
                {effectiveView.additional.length > 0 ? (
                  <div>
                    <p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">
                      Permisos adicionales
                    </p>
                    <ul className="space-y-1">
                      {effectiveView.additional.map((r) => (
                        <li key={r.permission} className="text-green-600">
                          + {r.label}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {effectiveView.deniedDirect.length > 0 ? (
                  <div>
                    <p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">
                      Restricciones individuales
                    </p>
                    <ul className="space-y-1">
                      {effectiveView.deniedDirect.map((r) => (
                        <li key={r.permission} className="text-red-500">
                          − {r.label}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
                    Acceso efectivo
                  </p>
                  <div className="space-y-3">
                    {OPERATIONAL_PERMISSION_DOMAINS.map((domain) => {
                      const rows = effectiveView.granted.filter((g) => g.domainId === domain.id);
                      if (!rows.length) return null;
                      return (
                        <div key={domain.id}>
                          <p className="font-medium">{domain.label}</p>
                          <p className="text-xs text-muted-foreground">
                            {rows.map((r) => r.label).join(" · ")}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  El plan del negocio limita qué módulos existen. Las extensiones instaladas no pueden otorgar capacidades fuera del plan.
                </p>
              </div>
            ) : null}
          </CrudEditorShell>

          {/* Role editor */}
          <CrudEditorShell
            open={roleEditorOpen}
            onOpenChange={setRoleEditorOpen}
            title={roleEditing ? `Editar rol · ${roleEditing.name}` : "Crear rol"}
            size="md"
            list={null}
            footer={
              <>
                <Button type="button" variant="outline" onClick={() => setRoleEditorOpen(false)} disabled={saving}>
                  Cancelar
                </Button>
                <Button type="button" onClick={() => void handleSaveRole()} disabled={saving}>
                  {saving ? "Guardando…" : "Guardar rol"}
                </Button>
              </>
            }
          >
            <div className="space-y-4">
              {roleEditing && (roleEditing.assignedUsers ?? 0) > 0 ? (
                <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
                  Este rol será aplicado a {roleEditing.assignedUsers} usuario
                  {(roleEditing.assignedUsers ?? 0) === 1 ? "" : "s"}.
                </p>
              ) : null}
              <div className="space-y-1.5">
                <FieldLabel required>Nombre</FieldLabel>
                <Input
                  value={roleForm.name}
                  onChange={(e) => setRoleForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Ej. Encargado de local"
                />
              </div>
              <div className="space-y-1.5">
                <FieldLabel>Descripción</FieldLabel>
                <Input
                  value={roleForm.description}
                  onChange={(e) => setRoleForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Qué puede hacer este rol"
                />
              </div>
              <div className="space-y-3">
                <p className="text-sm font-medium">Permisos</p>
                <p className="text-xs text-muted-foreground">
                  Para la mayoría alcanza con activarlo o no. Donde tiene sentido (por ejemplo Catálogo
                  u Órdenes) podés elegir el nivel: solo lectura, operar, o control total.
                </p>
                {OPERATIONAL_PERMISSION_DOMAINS.map((domain) => (
                  <div key={domain.id} className="rounded-md border border-border p-2">
                    <p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">
                      {domain.label}
                    </p>
                    <p className="mb-2 text-[11px] text-muted-foreground">{domain.description}</p>
                    <div className="space-y-1">
                      {domain.permissions.map((perm) => {
                        const checked = roleForm.permissions.includes(perm);
                        const usable = isPermissionUsable(perm, productFlags);
                        const spec = PERMISSION_CAPABILITY_SPECS[perm];
                        // SCRUM-477/478/479: la plantilla de rol (antes solo boolean) ahora
                        // también guarda el nivel elegido — mismo concepto (CapabilityLevel)
                        // que ya usa el override por usuario (SCRUM-149), no uno nuevo.
                        const selectedLevel = roleForm.permissionLevels[perm];
                        const displayLevel: CapabilityLevel =
                          selectedLevel ?? (checked ? spec.booleanEquivalentLevel : "none");

                        const handleLevelChange = (level: CapabilityLevel) => {
                          setRoleForm((f) => {
                            const permissions =
                              level === "none"
                                ? f.permissions.filter((p) => p !== perm)
                                : f.permissions.includes(perm)
                                  ? f.permissions
                                  : [...f.permissions, perm];
                            const permissionLevels = { ...f.permissionLevels };
                            if (level === "none") delete permissionLevels[perm];
                            else permissionLevels[perm] = level;
                            return { ...f, permissions, permissionLevels };
                          });
                        };

                        if (spec.supportsSubLevels) {
                          return (
                            <div
                              key={perm}
                              className={`flex items-center gap-2 text-sm ${usable ? "" : "opacity-50"}`}
                              title={usable ? undefined : "Esta función no está activada para tu negocio — no tiene efecto hasta que la actives"}
                            >
                              <span className="flex-1">{OPERATIONAL_PERMISSION_LABELS[perm]}</span>
                              <select
                                value={displayLevel}
                                disabled={!usable && displayLevel === "none"}
                                onChange={(e) => handleLevelChange(e.target.value as CapabilityLevel)}
                                className="h-7 rounded border border-input bg-transparent px-1.5 text-[11px] text-muted-foreground"
                                data-testid={`role-capability-level-select-${perm}`}
                              >
                                {(["none", "read", "write", "admin"] as CapabilityLevel[]).map((lvl) => (
                                  <option key={lvl} value={lvl}>
                                    {CAPABILITY_LEVEL_SHORT_LABELS[lvl]}
                                  </option>
                                ))}
                              </select>
                            </div>
                          );
                        }

                        return (
                          <label
                            key={perm}
                            className={`flex items-center gap-2 text-sm ${usable ? "" : "opacity-50"}`}
                            title={usable ? undefined : "Esta función no está activada para tu negocio — no tiene efecto hasta que la actives"}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={!usable && !checked}
                              onChange={(e) => {
                                setRoleForm((f) => ({
                                  ...f,
                                  permissions: e.target.checked
                                    ? [...f.permissions, perm]
                                    : f.permissions.filter((p) => p !== perm),
                                }));
                              }}
                            />
                            <span className="flex-1">{OPERATIONAL_PERMISSION_LABELS[perm]}</span>
                            <span className="text-[10px] text-muted-foreground">
                              {!usable
                                ? "No disponible"
                                : checked
                                  ? permissionAccessKindLabel(permissionAccessKind(perm))
                                  : "Sin acceso"}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CrudEditorShell>
        </div>
      )}
    </div>
  );
}
